/**
 * Legacy WooCommerce → new site 301 redirect resolver.
 *
 * Built from the real Google Search Console coverage export (2026-08-01):
 *   - 86 URLs reported as "Not found (404)"
 *   - 44 URLs reported as "Page with redirect"
 *
 * The old site was a multilingual WooCommerce install (WPML/Polylang) with
 * locale prefixes (/en/ /ar/ /fr/ /de/ /es/ /pt/ /ru/) and translated path
 * segments (produkt, produit, producto, produto, продукт, منتج ...).
 * The new site only serves ar/en and uses /product/<handle>.
 *
 * Anything that returns `null` is intentionally left as a 404 (WordPress
 * internals, tracking endpoints, admin author archives, raw media files).
 */

/** Locale prefixes that existed on the old WooCommerce site. */
const LEGACY_LOCALES = new Set(["en", "ar", "fr", "de", "es", "pt", "ru"]);

/** "product" in every language the old site used. */
const PRODUCT_SEGMENTS = new Set([
  "product",
  "products",
  "produkt",
  "produit",
  "producto",
  "produto",
  "продукт",
  "منتج",
]);

/** Category / brand archive segments → funnel to /shop. */
const ARCHIVE_SEGMENTS = new Set([
  "product-category",
  "produkt-kategorie",
  "categorie-produit",
  "categoria-producto",
  "categoria-produto",
  "продукт-категория",
  "категория",
  "تصنيف-المنتجات",
  "brand",
  "бренд",
  "marke",
  "marque",
  "marca",
  "العلامة التجارية",
  "product-tag",
  "produktkategorie",
  "produkt-tag",
  "shop",
  "store",
]);


/**
 * Old product slug → current Shopify handle.
 * Keys are lowercase, decoded, without locale prefix or trailing slash.
 */
const SLUG_MAP: Record<string, string> = {
  // ── The Blue Perfume Collection ───────────────────────────────
  "the-blue": "the-blue-perfume-collection",
  "the-blue-collection": "the-blue-perfume-collection",
  "the-blue-collection-perfume": "the-blue-perfume-collection",
  "lecmo-blue-collection": "the-blue-perfume-collection",
  "the-blue-perfume-collection": "the-blue-perfume-collection",
  "thd-blue-perfume-collection": "the-blue-perfume-collection",
  "thd-blue-perfume-": "the-blue-perfume-collection",

  // ── The Pink ─────────────────────────────────────────────────
  the_pink: "the-pink",
  "the-pink": "the-pink",

  // ── Orangy ───────────────────────────────────────────────────
  "orangy-collection": "orangy",
  "collection-orange": "orangy",
  orangy: "orangy",

  // ── The Brown Leather ────────────────────────────────────────
  "the-brown-leather": "the-brown-leather",
  "brown-leather-100ml-ar": "the-brown-leather",
  "lecmo-brown-leather-100ml": "the-brown-leather",
  "lecmo-brown-leather-100ml-en": "the-brown-leather",
  "browny-collection": "the-brown-leather",

  // ── The Purple (collection) ──────────────────────────────────
  "the-purple": "the-purple",
  "the-purple-ar": "the-purple",
  "the-purple-collection": "the-purple",

  // ── Purpleon (single) ────────────────────────────────────────
  purpleon: "purpleon",
  "purpleon-oriental-perfume": "purpleon",
  пурпурон: "purpleon",

  // ── Blacko Oud ───────────────────────────────────────────────
  blacko: "blacko-oud",
  "blacko-oud": "blacko-oud",
  "lecmo-black-oud-en": "blacko-oud",
  "lecmo-black-oud-ar": "blacko-oud",
  "ليكمو-بلاك-عود-100-ملليلتر": "blacko-oud",

  // ── Ramli ────────────────────────────────────────────────────
  ramli: "ramli",
  "ramli-leather": "ramli",

  // ── Red family ───────────────────────────────────────────────
  // `red-fresh` as a standalone product no longer exists (verified against the
  // production sitemap 2026-09-11); its scent now ships inside the drawer box.
  "red-red": "red-red",
  "red-red-collection": "red-red",
  "red-fresh": "red-misk-red-fresh-drawer-box",
  "lecmo-red-fresh-100ml": "red-misk-red-fresh-drawer-box",
  "red-misk": "red-misk-red-fresh-drawer-box",
  "red-misk-red-fresh-drawer-box": "red-misk-red-fresh-drawer-box",
  "lecmo-red-misk": "red-misk-red-fresh-drawer-box",
  "red-amber": "red-amber",

  // ── Direct handle passthroughs ───────────────────────────────
  verdant: "verdant",
  pistachio: "pistachio",
  "pistachio-gourmand-perfume": "pistachio",
  nemro: "nemro",
  ashmood: "ashmood",
  "the-blue-oud": "the-blue-oud",
  "tahaadow-collection": "tahaadow-collection",
  "grey-sweet-edp-perfume": "grey-sweet-edp-perfume",
  "the-blue-edp": "the-blue-edp",
  "the-blue-misk-edp-perfume": "the-blue-misk-edp-perfume",
  "the-blue-powdery-edp-perfume": "the-blue-powdery-edp-perfume",
  "lecmo-red-oud-edp-perfume": "lecmo-red-oud-edp-perfume",
  "the-maroony-edp-perfume": "the-maroony-edp-perfume",
};

/**
 * Product handles that actually exist on the live site (production sitemap,
 * verified 2026-09-11). A test asserts every SLUG_MAP value is in this set so a
 * legacy URL can never be redirected to a dead product page.
 */
export const LIVE_PRODUCT_HANDLES = new Set([
  "ashmood",
  "blacko-oud",
  "grey-sweet-edp-perfume",
  "lecmo-red-oud-edp-perfume",
  "nemro",
  "orangy",
  "pistachio",
  "purpleon",
  "ramli",
  "red-amber",
  "red-misk-red-fresh-drawer-box",
  "red-red",
  "tahaadow-collection",
  "the-blue-edp",
  "the-blue-misk-edp-perfume",
  "the-blue-oud",
  "the-blue-perfume-collection",
  "the-blue-powdery-edp-perfume",
  "the-brown-leather",
  "the-maroony-edp-perfume",
  "the-pink",
  "the-purple",
  "verdant",
]);

/** Exposed for the regression test only. */
export const LEGACY_SLUG_MAP: Readonly<Record<string, string>> = SLUG_MAP;

export type LegacyCapture = { sourcePath: string; locale: string | null };


/**
 * Old product slugs whose product no longer exists (discontinued or renamed
 * beyond a safe 1:1 guess). These go to /shop instead of a wrong product.
 */
const DISCONTINUED_SLUGS = new Set([
  "armady-collection",
  "lecmo-armadi-collection-en",
  "lecmo-armadi-collection",
  "lecmo-red-eau-de-parfum-en",
  "عود-العود",
  "blue-12ml",
  "sample-5ml-lecmo-ambassador-gift",
  "sample-10ml-super-star-ambassador-gift",
  "sample-2ml-lecmo-lover-gift",
]);

/** Exact legacy paths (after locale strip) → new path. */
const PAGE_MAP: Record<string, string> = {
  "": "/",
  home: "/",
  "guest-track-order-form": "/track-order",
  "track-order-form": "/track-order",
  "privacy-policy": "/privacy",
  "terms-and-conditions": "/terms",
  refund_returns: "/returns",
  "terms-conditions": "/terms",
  "return-policy": "/returns",
  "returns-policy": "/returns",
  "shipping-policy": "/shipping",
  partner: "/wholesale",
  partners: "/wholesale",
  "ambssador-program": "/ambassadors",
  "ambassador-program": "/ambassadors",
  branches: "/contact",

  outlets: "/contact",
  "contact-us": "/contact",
  "about-us": "/",
  blog: "/journal",
  news: "/journal",
  "my-account": "/account",
  cart: "/",
  checkout: "/",
  wishlist: "/account/wishlist",
};

/** Current public static paths: retired locale URLs keep the same destination. */
const PUBLIC_PAGE_PATHS = new Set([
  "contact", "faq", "shipping", "returns", "terms", "privacy",
  "ambassadors", "wholesale", "body-care-set",
]);

/** Verified current collections from the production sitemap (2026-09-07). */
const COLLECTION_MAP: Record<string, string> = {
  "perfume-gift-sets": "gift-set-perfume-collection",
  "gift-set-perfume-collection": "gift-set-perfume-collection",
  "lady-bag-perfume-set": "ladybag-collection",
  "ladybag-collection": "ladybag-collection",
  "single-perfume": "single-perfume",
  "oud-scent": "oud-scent",
  "new-product": "new-product",
};

/** Never redirect these — WordPress internals, tracking, raw assets. */
const DEAD_PREFIXES = [
  "wp-json",
  "wp-content",
  "wp-includes",
  "wp-admin",
  "wp-login.php",
  "xmlrpc.php",
  "feed",
  "author",
  "assets/mp4",
  "?add-to-cart",
];

function normalize(raw: string): string {
  let p = raw;
  try {
    p = decodeURIComponent(raw);
  } catch {
    /* keep raw when the path has malformed escapes */
  }
  return p
    .replace(/[?#].*$/, "")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "")
    .toLowerCase();

}

/**
 * Returns safe metadata for old-site URLs worth reviewing.
 * Covers every retired locale, plus old translated product/archive shapes.
 */
export function getLegacyCapture(raw: string): LegacyCapture | null {
  const path = normalize(raw);
  if (!path) return null;
  const segments = path.split("/").filter(Boolean);
  const first = segments[0] ?? "";
  const locale = LEGACY_LOCALES.has(first) ? first : null;
  const contentFirst = locale ? (segments[1] ?? "") : first;
  if (DEAD_PREFIXES.some((d) => contentFirst === d || contentFirst.startsWith(`${d}/`))) {
    return null;
  }
  const looksLegacy =
    locale !== null ||
    PRODUCT_SEGMENTS.has(contentFirst) ||
    ARCHIVE_SEGMENTS.has(contentFirst) ||
    contentFirst === "collection" ||
    contentFirst === "collections";
  if (!looksLegacy) return null;
  return {
    sourcePath: `/${path}`.slice(0, 500),
    locale,
  };
}

/** Arabic-script characters anywhere in the legacy path imply an Arabic page. */
const ARABIC_SCRIPT = /[\u0600-\u06FF]/;

/**
 * Keep the reader on the language they clicked in Google: legacy `/ar/...`
 * URLs (and Arabic-slug URLs) carry `?lang=ar` so the new site opens in Arabic
 * instead of falling back to IP/browser detection.
 */
function withLocale(target: string | null, arabic: boolean): string | null {
  if (!target || !arabic) return target;
  if (target.includes("lang=")) return target;
  return `${target}${target.includes("?") ? "&" : "?"}lang=ar`;
}

/**
 * Resolve a legacy URL path to its new location.
 * @param pathname incoming pathname (may include locale prefix / trailing slash)
 * @returns the new path to 301 to, or `null` to keep a genuine 404
 */
export function resolveLegacyRedirect(pathname: string): string | null {
  let path = normalize(pathname);

  // Wildcard crawl artifacts like "/ar/*" or "/wp-content/themes/woodmart/*"
  path = path.replace(/\/\*$/, "");

  if (!path) return null; // "/" is already valid

  if (DEAD_PREFIXES.some((d) => path === d || path.startsWith(`${d}/`))) {
    return null;
  }

  // Strip legacy locale prefix
  const segments = path.split("/").filter(Boolean);
  const arabic = segments[0] === "ar" || ARABIC_SCRIPT.test(path);
  if (segments.length && LEGACY_LOCALES.has(segments[0])) {
    segments.shift();
  }

  if (segments.length === 0) return withLocale("/", arabic);

  const [first, ...rest] = segments;

  if (DEAD_PREFIXES.some((d) => first === d)) return null;

  // Product URLs in any language
  if (PRODUCT_SEGMENTS.has(first) && rest.length) {
    const slug = rest[rest.length - 1] || rest[0];
    if (DISCONTINUED_SLUGS.has(slug)) return withLocale("/shop", arabic);
    const handle = SLUG_MAP[slug];
    if (handle) return withLocale(`/product/${handle}`, arabic);
    // Unknown product: never guess a specific product page, but keep the visitor
    // on the shop instead of a dead end. The catch-all already recorded the URL
    // in legacy_url_registry, so an admin can map it to an exact product later.
    return withLocale("/shop", arabic);

  }

  // Category / brand / tag archives and old shop pages
  if (ARCHIVE_SEGMENTS.has(first)) {
    // Use an equivalent collection when known; other archives keep /shop.
    const collection = COLLECTION_MAP[rest[0]];
    return withLocale(collection ? `/collection/${collection}` : "/shop", arabic);
  }

  if ((first === "collection" || first === "collections") && rest.length === 1) {
    const collection = COLLECTION_MAP[rest[0]];
    if (collection) return withLocale(`/collection/${collection}`, arabic);
  }

  // Known static pages
  const pageKey = segments.join("/");
  if (PUBLIC_PAGE_PATHS.has(pageKey)) return withLocale(`/${pageKey}`, arabic);
  if (PAGE_MAP[pageKey] !== undefined) return withLocale(PAGE_MAP[pageKey], arabic);
  if (PAGE_MAP[first] !== undefined) return withLocale(PAGE_MAP[first], arabic);

  // Paginated archives: ".../page/2"
  if (segments.includes("page")) return withLocale("/shop", arabic);

  return null;
}

